import { useMemo, useState, type FormEvent } from 'react';
import type { CwConnection, CwCustomerFilterEntry, CwCustomerOption } from '@catalyst-psa/shared';

import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { useCwCustomerOptions, useSetCwCustomerFilter } from './api.js';

interface CwCustomerFilterProps {
  connection: CwConnection | null;
  isAdmin: boolean;
  configured: boolean;
}

function inputClass(): string {
  return 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';
}

/**
 * Admin-only global customer filter. When one or more ConnectWise companies are
 * selected, every dataset sync is scoped to those companies (on top of the look-back
 * window); an empty selection syncs all customers. Options are listed live from the
 * ConnectWise API, not from previously-synced Catalyst customers.
 */
export function CwCustomerFilter({
  connection,
  isAdmin,
  configured,
}: CwCustomerFilterProps): JSX.Element | null {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<CwCustomerFilterEntry[] | null>(null);

  const saved = useMemo(() => connection?.customerFilter ?? [], [connection?.customerFilter]);
  const current = selected ?? saved;

  const optionsQuery = useCwCustomerOptions(search, isAdmin && configured);
  const setFilterMutation = useSetCwCustomerFilter();

  if (!isAdmin) return null;

  const toggle = (option: CwCustomerOption): void => {
    setSelected(() => {
      const base = current;
      const exists = base.some((entry) => entry.id === option.id);
      if (exists) return base.filter((entry) => entry.id !== option.id);
      return [...base, { id: option.id, name: option.name || option.identifier || option.id }];
    });
  };

  const remove = (id: string): void => {
    setSelected(current.filter((entry) => entry.id !== id));
  };

  const handleSearch = (event: FormEvent): void => {
    event.preventDefault();
    setSearch(searchInput.trim());
  };

  const handleSave = (): void => {
    setFilterMutation.mutate(
      { customers: current },
      { onSuccess: () => setSelected(null) },
    );
  };

  const dirty =
    selected !== null &&
    (selected.length !== saved.length ||
      selected.some((entry) => !saved.some((s) => s.id === entry.id)));

  const options = optionsQuery.data?.customers ?? [];

  return (
    <div className="mb-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-1 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-700">Customer sync filter</h2>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!dirty || setFilterMutation.isPending}
        >
          {setFilterMutation.isPending ? 'Saving…' : 'Save filter'}
        </Button>
      </div>
      <p className="mb-4 text-xs text-slate-500">
        Limit every dataset sync to the selected ConnectWise customers (combined with the
        look-back window). Leave empty to sync all customers.
      </p>

      {!configured ? (
        <p className="text-sm text-slate-500">
          Configure and save the ConnectWise connection to choose customers.
        </p>
      ) : (
        <>
          {current.length > 0 ? (
            <div className="mb-4 flex flex-wrap gap-2">
              {current.map((entry) => (
                <span
                  key={entry.id}
                  className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700"
                >
                  {entry.name || entry.id}
                  <button
                    type="button"
                    onClick={() => remove(entry.id)}
                    className="text-brand-500 hover:text-brand-700"
                    aria-label={`Remove ${entry.name || entry.id}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <p className="mb-4 text-sm text-slate-500">
              No customers selected — all customers will sync.
            </p>
          )}

          <form onSubmit={handleSearch} className="mb-3 flex items-center gap-2">
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search ConnectWise customers…"
              className={inputClass()}
            />
            <Button type="submit" variant="secondary">
              Search
            </Button>
          </form>

          {optionsQuery.isLoading ? (
            <p className="text-sm text-slate-500">Loading customers…</p>
          ) : optionsQuery.isError ? (
            <p className="text-sm text-rose-600">Failed to load customers from ConnectWise.</p>
          ) : options.length === 0 ? (
            <p className="text-sm text-slate-500">No customers found.</p>
          ) : (
            <ul className="max-h-64 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200">
              {options.map((option) => {
                const checked = current.some((entry) => entry.id === option.id);
                return (
                  <li key={option.id}>
                    <label className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(option)}
                        className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                      />
                      <span className="font-medium text-slate-800">{option.name || option.id}</span>
                      {option.identifier ? (
                        <Badge tone="gray">{option.identifier}</Badge>
                      ) : null}
                    </label>
                  </li>
                );
              })}
            </ul>
          )}

          {setFilterMutation.isError ? (
            <p className="mt-3 text-sm text-rose-600">Failed to save the customer filter.</p>
          ) : null}
        </>
      )}
    </div>
  );
}
